import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { insuranceTypes, findInsuranceBySlug } from "../data/insurances";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const BR_STATES = [
  "AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"
];

export default function CotacaoPage() {
  const [searchParams] = useSearchParams();
  const preselect = searchParams.get("tipo");
  const navigate = useNavigate();

  // Step: 0 = choose type, 1 = personal data, 2 = specific data, 3 = review, 4 = success
  const [step, setStep] = useState(preselect && findInsuranceBySlug(preselect) ? 1 : 0);
  const [selectedType, setSelectedType] = useState(preselect || "");
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  const [personal, setPersonal] = useState({
    full_name: "",
    email: "",
    whatsapp: "",
    city: "",
    state: "",
  });
  const [details, setDetails] = useState({});
  const [notes, setNotes] = useState("");
  const [consentLgpd, setConsentLgpd] = useState(false);

  const insurance = selectedType ? findInsuranceBySlug(selectedType) : null;
  const totalSteps = 4;
  const progressPct = ((step + (step >= 4 ? 0 : 0)) / (totalSteps - 1)) * 100;

  const canGoNext = () => {
    if (step === 0) return !!selectedType;
    if (step === 1) {
      return (
        personal.full_name.trim().length >= 3 &&
        personal.email.includes("@") &&
        personal.whatsapp.replace(/\D/g, "").length >= 10 &&
        personal.city.trim().length >= 2 &&
        personal.state.length === 2
      );
    }
    if (step === 2) {
      const required = insurance.quoteFields.filter((f) => f.required);
      return required.every((f) => {
        const v = details[f.name];
        return v !== undefined && String(v).trim().length > 0;
      });
    }
    if (step === 3) return consentLgpd;
    return false;
  };

  const onNext = () => setStep((s) => Math.min(s + 1, 4));
  const onBack = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/quotes", {
        insurance_type: selectedType,
        ...personal,
        details,
        notes: notes || null,
        consent_lgpd: consentLgpd,
      });
      setSubmittedId(data.id);
      toast.success("Cotação enviada com sucesso!");
      setStep(4);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-14 md:py-20 bg-surface min-h-[80vh]" data-testid="cotacao-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="text-sm text-navy/60 hover:text-lego font-semibold inline-flex items-center gap-2"
          >
            <i className="fa-solid fa-arrow-left text-xs" /> Voltar para o início
          </Link>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-4">
            Cotação {insurance ? insurance.name : "de Seguro"}
          </h1>
          <p className="text-navy/70 mt-2">
            Cotação rápida e sem compromisso. 100% gratuita.
          </p>
        </div>

        {step < 4 && (
          <div className="mb-8">
            <div className="flex justify-between text-xs font-semibold text-navy/60 mb-2">
              <span>Passo {step + 1} de {totalSteps}</span>
              <span>{Math.round(progressPct)}%</span>
            </div>
            <Progress value={progressPct} className="h-2" />
          </div>
        )}

        <div className="bg-white rounded-3xl p-6 md:p-10 shadow-soft border border-navy-100">
          {/* STEP 0: choose insurance type */}
          {step === 0 && (
            <div data-testid="step-choose-type">
              <h2 className="font-heading text-2xl font-bold text-navy mb-1">
                Qual seguro você quer?
              </h2>
              <p className="text-sm text-navy/60 mb-6">Escolha um dos seguros para começar.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {insuranceTypes.map((i) => (
                  <button
                    key={i.slug}
                    type="button"
                    onClick={() => setSelectedType(i.slug)}
                    className={`text-left p-4 rounded-2xl border-2 transition-all ${
                      selectedType === i.slug
                        ? "border-lego bg-lego/5"
                        : "border-navy-100 hover:border-navy/30"
                    }`}
                    data-testid={`choose-type-${i.slug}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                      selectedType === i.slug ? "bg-lego text-white" : "bg-navy-50 text-navy"
                    }`}>
                      <i className={`fa-solid ${i.icon}`} />
                    </div>
                    <p className="font-heading font-bold text-navy text-sm">{i.short}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 1: personal */}
          {step === 1 && (
            <div className="space-y-5" data-testid="step-personal">
              <h2 className="font-heading text-2xl font-bold text-navy">Seus dados de contato</h2>
              <p className="text-sm text-navy/60 -mt-3">
                Vamos precisar deles para enviar sua cotação.
              </p>
              <div>
                <Label>Nome completo *</Label>
                <Input
                  value={personal.full_name}
                  onChange={(e) => setPersonal({ ...personal, full_name: e.target.value })}
                  placeholder="Como gostaria de ser chamado(a)?"
                  data-testid="input-full-name"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>E-mail *</Label>
                  <Input
                    type="email"
                    value={personal.email}
                    onChange={(e) => setPersonal({ ...personal, email: e.target.value })}
                    placeholder="voce@email.com"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label>WhatsApp *</Label>
                  <Input
                    value={personal.whatsapp}
                    onChange={(e) => setPersonal({ ...personal, whatsapp: e.target.value })}
                    placeholder="(11) 99999-9999"
                    data-testid="input-whatsapp"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label>Cidade *</Label>
                  <Input
                    value={personal.city}
                    onChange={(e) => setPersonal({ ...personal, city: e.target.value })}
                    placeholder="Ex.: Jales"
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <Label>UF *</Label>
                  <Select
                    value={personal.state}
                    onValueChange={(v) => setPersonal({ ...personal, state: v })}
                  >
                    <SelectTrigger data-testid="select-state">
                      <SelectValue placeholder="UF" />
                    </SelectTrigger>
                    <SelectContent>
                      {BR_STATES.map((uf) => (
                        <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: specific */}
          {step === 2 && insurance && (
            <div className="space-y-5" data-testid="step-specific">
              <h2 className="font-heading text-2xl font-bold text-navy">
                Detalhes do {insurance.short}
              </h2>
              <p className="text-sm text-navy/60 -mt-3">
                Essas informações ajudam a calcular a melhor opção para você.
              </p>
              {insurance.quoteFields.map((f) => (
                <div key={f.name}>
                  <Label>
                    {f.label} {f.required && "*"}
                  </Label>
                  {f.type === "select" ? (
                    <Select
                      value={details[f.name] || ""}
                      onValueChange={(v) => setDetails({ ...details, [f.name]: v })}
                    >
                      <SelectTrigger data-testid={`input-${f.name}`}>
                        <SelectValue placeholder={`Selecione ${f.label.toLowerCase()}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {f.options.map((op) => (
                          <SelectItem key={op} value={op}>{op}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={f.type}
                      value={details[f.name] || ""}
                      onChange={(e) => setDetails({ ...details, [f.name]: e.target.value })}
                      placeholder={f.placeholder || ""}
                      data-testid={`input-${f.name}`}
                    />
                  )}
                </div>
              ))}

              <div>
                <Label>Observações (opcional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Algo que queira nos contar? Preferências de horário, necessidades especiais..."
                  data-testid="input-notes"
                />
              </div>
            </div>
          )}

          {/* STEP 3: review */}
          {step === 3 && insurance && (
            <div className="space-y-5" data-testid="step-review">
              <h2 className="font-heading text-2xl font-bold text-navy">Tudo certo?</h2>
              <p className="text-sm text-navy/60 -mt-3">
                Revise suas informações antes de enviar.
              </p>

              <div className="space-y-4">
                <SummaryRow label="Seguro" value={insurance.name} />
                <SummaryRow label="Nome" value={personal.full_name} />
                <SummaryRow label="E-mail" value={personal.email} />
                <SummaryRow label="WhatsApp" value={personal.whatsapp} />
                <SummaryRow label="Localização" value={`${personal.city}/${personal.state}`} />
                <div className="pt-3 border-t border-navy-100">
                  <p className="text-xs uppercase tracking-widest text-navy/50 font-bold mb-3">
                    Detalhes
                  </p>
                  {insurance.quoteFields.map((f) => (
                    <SummaryRow
                      key={f.name}
                      label={f.label}
                      value={details[f.name] || "—"}
                    />
                  ))}
                  {notes && <SummaryRow label="Observações" value={notes} />}
                </div>
              </div>

              <div className="bg-navy-50 rounded-2xl p-5 flex gap-3">
                <Checkbox
                  id="lgpd"
                  checked={consentLgpd}
                  onCheckedChange={setConsentLgpd}
                  className="mt-1"
                  data-testid="checkbox-lgpd"
                />
                <Label htmlFor="lgpd" className="text-sm text-navy/80 leading-relaxed cursor-pointer font-normal">
                  Concordo em compartilhar meus dados com a Safe Life para receber a cotação. Li e aceito a{" "}
                  <Link to="/politica-privacidade" className="text-lego underline font-semibold">
                    Política de Privacidade
                  </Link>{" "}
                  conforme a LGPD.
                </Label>
              </div>
            </div>
          )}

          {/* STEP 4: success */}
          {step === 4 && (
            <div className="text-center py-8" data-testid="step-success">
              <div className="w-24 h-24 bg-lego/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="fa-solid fa-heart text-lego text-4xl" />
              </div>
              <h2 className="font-heading text-3xl font-extrabold text-navy mb-3">
                Recebemos sua solicitação!
              </h2>
              <p className="text-navy/70 max-w-md mx-auto leading-relaxed">
                Um consultor especialista da Safe Life entrará em contato pelo WhatsApp ou e-mail{" "}
                <strong className="text-brand">o mais rápido possível</strong> (em horário comercial).
              </p>
              {submittedId && (
                <p className="text-xs text-navy/40 mt-4 font-mono">
                  Protocolo: {submittedId.slice(0, 8).toUpperCase()}
                </p>
              )}
              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <Button
                  asChild
                  className="bg-navy hover:bg-navy-900 text-white rounded-full h-12 px-6 font-semibold"
                  data-testid="success-home-btn"
                >
                  <Link to="/">Voltar ao início</Link>
                </Button>
                <Button
                  onClick={() => {
                    setStep(0);
                    setSelectedType("");
                    setPersonal({ full_name: "", email: "", whatsapp: "", city: "", state: "" });
                    setDetails({});
                    setNotes("");
                    setConsentLgpd(false);
                    setSubmittedId(null);
                  }}
                  variant="outline"
                  className="rounded-full h-12 px-6 border-2 border-navy text-navy font-semibold"
                  data-testid="success-new-quote-btn"
                >
                  Nova cotação
                </Button>
              </div>
            </div>
          )}

          {/* Navigation */}
          {step < 3 && step >= 0 && (
            <div className="flex justify-between items-center mt-10 pt-6 border-t border-navy-100">
              <Button
                variant="ghost"
                onClick={onBack}
                disabled={step === 0}
                className="text-navy"
                data-testid="nav-back-btn"
              >
                <i className="fa-solid fa-arrow-left mr-2" /> Voltar
              </Button>
              <Button
                onClick={onNext}
                disabled={!canGoNext()}
                className="bg-brand hover:bg-brand-600 active:bg-brand-700 text-white rounded-full h-12 px-8 font-semibold disabled:opacity-40"
                data-testid="nav-next-btn"
              >
                Continuar <i className="fa-solid fa-arrow-right ml-2" />
              </Button>
            </div>
          )}
          {step === 3 && (
            <div className="flex justify-between items-center mt-10 pt-6 border-t border-navy-100">
              <Button
                variant="ghost"
                onClick={onBack}
                className="text-navy"
                data-testid="nav-back-btn"
              >
                <i className="fa-solid fa-arrow-left mr-2" /> Voltar
              </Button>
              <Button
                onClick={submit}
                disabled={!canGoNext() || submitting}
                className="bg-brand hover:bg-brand-600 active:bg-brand-700 text-white rounded-full h-12 px-8 font-semibold disabled:opacity-40"
                data-testid="submit-quote-btn"
              >
                {submitting ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2" /> Enviando...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-shield-heart mr-2" /> Enviar cotação
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between items-start gap-4 py-2">
      <span className="text-sm text-navy/60 font-semibold">{label}</span>
      <span className="text-sm text-navy text-right font-medium max-w-[60%] break-words">{String(value)}</span>
    </div>
  );
}
